import type { ChannelLocationStatusDto } from '@up2stay/up2promo-api-client';

export type ChannelSyncStatus = ChannelLocationStatusDto['syncStatus'];

export const GOOGLE_PROVIDER = 'google';

export function googleChannels(
  channels: ChannelLocationStatusDto[],
): ChannelLocationStatusDto[] {
  return channels.filter((c) => c.provider === GOOGLE_PROVIDER && c.linked);
}

export function channelsInScope(
  channels: ChannelLocationStatusDto[],
  scopeLocationId?: string,
): ChannelLocationStatusDto[] {
  const google = googleChannels(channels);
  if (!scopeLocationId) return google;
  return google.filter((c) => c.locationId === scopeLocationId);
}

export function isActiveSyncStatus(status: ChannelSyncStatus): boolean {
  return status === 'pending' || status === 'syncing' || status === 'failed';
}

export type ReputationBannerState = {
  visible: boolean;
  variant:
    | 'pending'
    | 'syncing'
    | 'refreshing'
    | 'failed'
    | 'reanalysisScheduled'
    | 'reanalyzing'
    | null;
  /** High-level banner grouping for UI labels. */
  category: 'google' | 'insights' | null;
  affectedLocationIds: string[];
  pendingAnalysisCount: number;
  /** Earliest scheduled re-analysis time in scope (ISO), if any. */
  scheduledReanalysisAt: string | null;
};

/** True once this link has finished at least one full review import. */
export function hasInitialSyncCompleted(
  channel: Pick<ChannelLocationStatusDto, 'lastFullSyncAt'>,
): boolean {
  return Boolean(channel.lastFullSyncAt);
}

function hasScheduledReanalysis(
  channel: ChannelLocationStatusDto,
  now = Date.now(),
): boolean {
  if (!channel.taxonomyReanalysisDueAt) return false;
  return new Date(channel.taxonomyReanalysisDueAt).getTime() > now;
}

function hasPendingAnalysis(channel: ChannelLocationStatusDto): boolean {
  return (channel.pendingAnalysisCount ?? 0) > 0;
}

function sumPendingAnalysis(channels: ChannelLocationStatusDto[]): number {
  return channels.reduce((sum, c) => sum + (c.pendingAnalysisCount ?? 0), 0);
}

function earliestScheduledReanalysis(
  channels: ChannelLocationStatusDto[],
): string | null {
  let earliest: number | null = null;
  for (const channel of channels) {
    if (!channel.taxonomyReanalysisDueAt) continue;
    const ms = new Date(channel.taxonomyReanalysisDueAt).getTime();
    if (earliest == null || ms < earliest) earliest = ms;
  }
  return earliest != null ? new Date(earliest).toISOString() : null;
}

const emptyBanner: ReputationBannerState = {
  visible: false,
  variant: null,
  category: null,
  affectedLocationIds: [],
  pendingAnalysisCount: 0,
  scheduledReanalysisAt: null,
};

export function resolveReputationBannerState(
  channels: ChannelLocationStatusDto[],
  scopeLocationId?: string,
): ReputationBannerState {
  const scoped = channelsInScope(channels, scopeLocationId);

  if (scopeLocationId && scoped.length === 0) {
    return emptyBanner;
  }

  const syncing = scoped.filter((c) => c.syncStatus === 'syncing');
  if (syncing.length > 0) {
    const initialSyncInProgress = syncing.some(
      (c) => !hasInitialSyncCompleted(c),
    );
    if (initialSyncInProgress) {
      return {
        visible: true,
        variant: 'syncing',
        category: 'google',
        affectedLocationIds: syncing
          .filter((c) => !hasInitialSyncCompleted(c))
          .map((c) => c.locationId),
        pendingAnalysisCount: sumPendingAnalysis(scoped),
        scheduledReanalysisAt: earliestScheduledReanalysis(scoped),
      };
    }
    // Scheduled/cron refresh after first import — silent background work.
  }

  const failed = scoped.filter((c) => c.syncStatus === 'failed');
  if (failed.length > 0) {
    return {
      visible: true,
      variant: 'failed',
      category: 'google',
      affectedLocationIds: failed.map((c) => c.locationId),
      pendingAnalysisCount: sumPendingAnalysis(scoped),
      scheduledReanalysisAt: earliestScheduledReanalysis(scoped),
    };
  }

  const pending = scoped.filter((c) => c.syncStatus === 'pending');
  if (pending.length > 0) {
    return {
      visible: true,
      variant: 'pending',
      category: 'google',
      affectedLocationIds: pending.map((c) => c.locationId),
      pendingAnalysisCount: sumPendingAnalysis(scoped),
      scheduledReanalysisAt: earliestScheduledReanalysis(scoped),
    };
  }

  const scheduled = scoped.filter((c) => hasScheduledReanalysis(c));
  if (scheduled.length > 0) {
    return {
      visible: true,
      variant: 'reanalysisScheduled',
      category: 'insights',
      affectedLocationIds: scheduled.map((c) => c.locationId),
      pendingAnalysisCount: sumPendingAnalysis(scoped),
      scheduledReanalysisAt: earliestScheduledReanalysis(scoped),
    };
  }

  const reanalyzing = scoped.filter(
    (c) => hasPendingAnalysis(c) && hasInitialSyncCompleted(c),
  );
  if (reanalyzing.length > 0) {
    return {
      visible: true,
      variant: 'reanalyzing',
      category: 'insights',
      affectedLocationIds: reanalyzing.map((c) => c.locationId),
      pendingAnalysisCount: sumPendingAnalysis(reanalyzing),
      scheduledReanalysisAt: null,
    };
  }

  return emptyBanner;
}

/** True when at least one scoped location has a linked Google review channel. */
export function isScopeLinked(
  channels: ChannelLocationStatusDto[],
  scopeLocationId?: string,
): boolean {
  return channelsInScope(channels, scopeLocationId).length > 0;
}

/** Inbox + dashboard KPIs: show real data once every scoped link has completed a full sync. */
export function isSyncCompleteForScope(
  channels: ChannelLocationStatusDto[],
  scopeLocationId?: string,
): boolean {
  const scoped = channelsInScope(channels, scopeLocationId);
  if (scoped.length === 0) return false;
  return scoped.every((c) => hasInitialSyncCompleted(c));
}

export function shouldPollChannelLocations(
  channels: ChannelLocationStatusDto[],
): boolean {
  return googleChannels(channels).some((c) => {
    if (c.syncStatus === 'syncing' && hasInitialSyncCompleted(c)) {
      return false;
    }
    return (
      isActiveSyncStatus(c.syncStatus) ||
      hasScheduledReanalysis(c) ||
      hasPendingAnalysis(c)
    );
  });
}
